import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  XMarkIcon, 
  CalendarDaysIcon,
  ClockIcon,
  DocumentTextIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface LeaveType {
  id: number
  name: string
  code: string
  maxDaysPerYear: number | null
  isPaid: boolean
  requiresApproval: boolean
}

interface LeaveTypesResponse {
  leaveTypes: LeaveType[]
}

interface LeaveBalance {
  id: number
  allocatedDays: number
  usedDays: number
  pendingDays: number
  carriedForwardDays: number
  availableDays: number
  leaveType: LeaveType
}

interface LeaveBalancesResponse {
  balances: LeaveBalance[]
  year: number
}

interface LeaveRequestFormData {
  leaveTypeId: string
  startDate: string
  endDate: string
  reason: string
}

interface LeaveRequestModalProps {
  onClose: () => void
  onSuccess: () => void
}

const LeaveRequestModal: React.FC<LeaveRequestModalProps> = ({ onClose, onSuccess }) => {
  const { user } = useAuth()
  const [formData, setFormData] = useState<LeaveRequestFormData>({
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    reason: ''
  })
  const [calculatedDays, setCalculatedDays] = useState<number>(0)

  const queryClient = useQueryClient()

  // Get active leave types for the dropdown
  const { data: leaveTypesData, isLoading: leaveTypesLoading } = useQuery<LeaveTypesResponse>({
    queryKey: ['activeLeaveTypes'],
    queryFn: async () => {
      const response = await api.get('/leaves/types/active')
      return response.data.data
    }
  })

  // Get current user's leave balances
  const { data: balancesData } = useQuery<LeaveBalancesResponse>({
    queryKey: ['currentUserLeaveBalances'],
    queryFn: async () => {
      const response = await api.get('/leaves/balances/my')
      return response.data.data
    },
    enabled: user?.role === 'employee'
  })

  // Create leave request mutation
  const createRequestMutation = useMutation({
    mutationFn: async (data: Omit<LeaveRequestFormData, 'leaveTypeId'> & { leaveTypeId: number }) => {
      const response = await api.post('/leaves/requests', data)
      return response.data
    },
    onSuccess: () => {
      toast.success('Leave request submitted successfully')
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] })
      queryClient.invalidateQueries({ queryKey: ['currentUserLeaveBalances'] })
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to submit leave request')
    }
  })

  // Calculate working days between dates (excluding weekends)
  const calculateWorkingDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0

    const start = new Date(startDate)
    const end = new Date(endDate)
    let count = 0

    const currentDate = new Date(start)
    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay()
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Sunday (0) or Saturday (6)
        count++
      }
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return count
  }

  // Update calculated days when dates change
  React.useEffect(() => {
    const days = calculateWorkingDays(formData.startDate, formData.endDate)
    setCalculatedDays(days)
  }, [formData.startDate, formData.endDate])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.leaveTypeId) {
      toast.error('Please select a leave type')
      return
    }

    if (!formData.startDate || !formData.endDate) {
      toast.error('Please select start and end dates')
      return
    }

    if (calculatedDays <= 0) {
      toast.error('Leave request must include at least one working day')
      return
    }

    // Check available balance if we have balance data
    if (balancesData) {
      const selectedBalance = balancesData.balances.find(
        b => b.leaveType.id === parseInt(formData.leaveTypeId)
      )
      
      if (selectedBalance && calculatedDays > selectedBalance.availableDays) {
        toast.error(`Insufficient leave balance. Available: ${selectedBalance.availableDays} days, Requested: ${calculatedDays} days`)
        return
      }
    }

    createRequestMutation.mutate({
      leaveTypeId: parseInt(formData.leaveTypeId),
      startDate: formData.startDate,
      endDate: formData.endDate,
      reason: formData.reason
    })
  }

  const selectedLeaveType = leaveTypesData?.leaveTypes.find(
    lt => lt.id === parseInt(formData.leaveTypeId)
  )

  const selectedBalance = balancesData?.balances.find(
    b => b.leaveType.id === parseInt(formData.leaveTypeId)
  )

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0]

  // Colors from your theme
  // const colors = {
  //   primary: "#63d400",
  //   dark: "#000065",
  //   light: "#f8fafc"
  // }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#000065] text-white">
              <CalendarDaysIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                New Leave Request
              </h3>
              <p className="text-sm text-gray-500">
                Submit a new leave request
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Leave Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Leave Type *
            </label>
            {leaveTypesLoading ? (
              <div className="animate-pulse bg-gray-200 h-12 rounded-xl"></div>
            ) : (
              <select
                value={formData.leaveTypeId}
                onChange={(e) => setFormData({ ...formData, leaveTypeId: e.target.value })}
                className="block w-full px-4 py-3 rounded-xl border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#63d400] focus:border-[#63d400] transition-colors text-sm appearance-none bg-white"
                required
              >
                <option value="">Select leave type...</option>
                {leaveTypesData?.leaveTypes.map((leaveType) => (
                  <option key={leaveType.id} value={leaveType.id}>
                    {leaveType.name} ({leaveType.code})
                    {leaveType.maxDaysPerYear && ` - Max ${leaveType.maxDaysPerYear} days/year`}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Leave Balance Info */}
          {selectedBalance && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <ClockIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">
                    Leave Balance
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700 font-medium">Available:</span>
                      <span className="text-blue-800 ml-2">{selectedBalance.availableDays} days</span>
                    </div>
                    <div>
                      <span className="text-blue-600">Allocated:</span>
                      <span className="text-blue-700 ml-2">{selectedBalance.allocatedDays} days</span>
                    </div>
                    <div>
                      <span className="text-blue-600">Used:</span>
                      <span className="text-blue-700 ml-2">{selectedBalance.usedDays} days</span>
                    </div>
                    <div>
                      <span className="text-blue-600">Pending:</span>
                      <span className="text-blue-700 ml-2">{selectedBalance.pendingDays} days</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Date Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Start Date *
              </label>
              <div className="relative">
                <CalendarDaysIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  min={today}
                  className="block w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#63d400] focus:border-[#63d400] transition-colors text-sm"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                End Date *
              </label>
              <div className="relative">
                <CalendarDaysIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  min={formData.startDate || today}
                  className="block w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#63d400] focus:border-[#63d400] transition-colors text-sm"
                  required
                />
              </div>
            </div>
          </div>

          {/* Calculated Days */}
          {calculatedDays > 0 && (
            <div className={`p-4 border rounded-xl ${
              selectedBalance && calculatedDays > selectedBalance.availableDays 
                ? 'bg-red-50 border-red-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center space-x-3">
                <CheckBadgeIcon className={`h-5 w-5 ${
                  selectedBalance && calculatedDays > selectedBalance.availableDays 
                    ? 'text-red-600' 
                    : 'text-green-600'
                }`} />
                <div>
                  <span className={`text-sm font-semibold ${
                    selectedBalance && calculatedDays > selectedBalance.availableDays 
                      ? 'text-red-900' 
                      : 'text-green-900'
                  }`}>
                    Working Days: {calculatedDays} day{calculatedDays !== 1 ? 's' : ''}
                  </span>
                  {selectedBalance && calculatedDays > selectedBalance.availableDays && (
                    <p className="text-red-700 text-sm mt-1">
                      Exceeds available balance by {calculatedDays - selectedBalance.availableDays} days
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Reason
              <span className="text-gray-400 text-sm font-normal ml-1">(Optional)</span>
            </label>
            <div className="relative">
              <DocumentTextIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={4}
                className="block w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 shadow-sm focus:ring-2 focus:ring-[#63d400] focus:border-[#63d400] transition-colors text-sm resize-none"
                placeholder="Provide a reason for your leave request..."
              />
            </div>
          </div>

          {/* Leave Type Info */}
          {selectedLeaveType && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <DocumentTextIcon className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    Leave Type Information
                  </h4>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full ${
                      selectedLeaveType.isPaid 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedLeaveType.isPaid ? 'Paid Leave' : 'Unpaid Leave'}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full ${
                      selectedLeaveType.requiresApproval 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedLeaveType.requiresApproval ? 'Requires Approval' : 'Auto-Approved'}
                    </span>
                    {selectedLeaveType.maxDaysPerYear && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800">
                        Max {selectedLeaveType.maxDaysPerYear} days/year
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createRequestMutation.isPending || calculatedDays <= 0 || (selectedBalance && calculatedDays > selectedBalance.availableDays)}
              className="px-6 py-3 text-sm font-medium text-white bg-[#63d400] border border-transparent rounded-xl hover:bg-[#52b800] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
            >
              {createRequestMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <CheckBadgeIcon className="w-4 h-4" />
                  <span>Submit Request</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LeaveRequestModal